import type { MenuJob } from 'src/utils/workspace/menu-jobs';
import type { MenuSet } from 'src/utils/workspace/menu-sets';
import type { AuthUser } from 'src/auth/context/auth-context-value';
import type { AnalysisTab } from 'src/sections/search-all-analysis/constants/search-all-analysis';

import Box from '@mui/material/Box';

import { paths } from 'src/routes/paths';

import { findCurrentWorkspace } from 'src/utils/util-user';
import { planViewerOfUser } from 'src/utils/plan/plan-tier';
import { canReadManual } from 'src/utils/manual/manual-path';
import { toTrackIntentHref } from 'src/utils/workspace/track-intent';
import { resolveSubscriptionPlanId } from 'src/utils/plan/plan-utils';
import { buildEffectivePlanConfig } from 'src/utils/plan/plan-access';
import { toCurrentWorkspaceId } from 'src/utils/workspace/last-workspace-id';
import { isSearchTrendFeatureWorkspace } from 'src/utils/workspace/feature-workspace';
import { hasWorkspaceLab, LAB_POST_ANALYSIS } from 'src/utils/workspace/workspace-labs';
import { toKeywordMonitoringHref, sortKeywordCampaignsByCreatedAt } from 'src/utils/campaign/keyword-campaign-order';

import { LOCK_ICON } from 'src/theme/ui-tokens';
import { SET } from 'src/constants/track-set-name';
import { IPlanService } from 'src/constants/plan-service';

import { Iconify } from 'src/components/iconify';
import { CatchMomentsIconSX } from 'src/components/icon';

import { ANALYSIS_TABS, ANALYSIS_TAB_LABELS } from 'src/sections/search-all-analysis/constants/search-all-analysis';

// ----------------------------------------------------------------------

// 아이콘 스타일 상수
const ICON_STYLE = { width: '20px !important', height: '20px !important' };

/**
 * 지금 플랜으로 못 여는 줄에 붙는 **자물쇠**.
 *
 * 🔑 「어느 플랜이면 열리나」(B·G)를 적지 않는다 — 줄마다 다른 글자가 붙으면 메뉴가
 *   요금표가 된다. 못 쓴다는 사실만 알리고, 어느 플랜인지는 눌렀을 때 안내가 말한다.
 */
const PLAN_LOCK_MARK = (
  <Box
    component="span"
    sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.disabled' }}
  >
    <Iconify icon={LOCK_ICON} width={15} />
  </Box>
);


/**
 * 「추적 관리」 옆 **초기 설정 진행** 표 — 「3/4」. 값은 `useOnboardingBadge()` 가 짓고, 다 했으면 오지 않는다.
 * ⚠️ 자물쇠가 붙은 줄(만료)에는 달지 않는다 — 자물쇠가 이긴다.
 */
const progressMark = (text: string) => (
  <Box component="span" sx={{ typography: 'uiLabel', color: 'primary.main', fontVariantNumeric: 'tabular-nums' }}>
    {text}
  </Box>
);

/**
 * RESEARCH 표제 줄 오른쪽 끝의 **이번 달 셈** — 「0/30」. 표제와 같은 글꼴 · 회색(남은 몫이지 할 일이 아니다).
 */
const usageMark = (text: string) => (
  <Box component="span" sx={{ typography: 'uiLabel', color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }} aria-label={`이번 달 Research ${text}회`}>
    {text}
  </Box>
);

/**
 * 못 여는 줄에 자물쇠를 붙입니다 — **`disabled` 하나만 본다.**
 *
 * 🔑 관문 판정은 `getAccessibleMenuItems` 가 이미 끝냈다. 여기서 플랜을 다시 재면
 *   두 곳이 따로 판정해 어긋난다 — 이 함수는 그 결과에 표시만 얹는다.
 * @param item `disabled` 가 정해진 뒤의 메뉴 항목
 */
const withPlanLock = <T extends Record<string, any> | null | undefined>(item: T) => {
  if (!item || !item.disabled) return item;

  return { ...item, info: PLAN_LOCK_MARK };
};

// 네비게이션 아이콘 정의
const ICONS = {
  search: CatchMomentsIconSX('menu-search', ICON_STYLE),
  keywordMonitoring: CatchMomentsIconSX('menu-keywordmonitoring', ICON_STYLE),
  influencer: CatchMomentsIconSX('menu-influencerlist', ICON_STYLE),
  brand: CatchMomentsIconSX('menu-brandmention', ICON_STYLE),
  blog: CatchMomentsIconSX('blog', ICON_STYLE),
  campaign: CatchMomentsIconSX('menu-influencermonitoring', ICON_STYLE),
  admin: CatchMomentsIconSX('crown', ICON_STYLE),
  file: CatchMomentsIconSX('file', ICON_STYLE),
  setting: CatchMomentsIconSX('menu-setting', ICON_STYLE),
  setup: CatchMomentsIconSX('setup', ICON_STYLE),
  sales: CatchMomentsIconSX('target', ICON_STYLE),
  help: CatchMomentsIconSX('help', ICON_STYLE),
  labs: CatchMomentsIconSX('menu-labs', ICON_STYLE),
  mall: CatchMomentsIconSX('menu-mall', ICON_STYLE),
  account: CatchMomentsIconSX('menu-brandaccount', ICON_STYLE),
  bookmark: CatchMomentsIconSX('menu-bookmark', ICON_STYLE),
  thumbnail: CatchMomentsIconSX('menu-thumbnail', ICON_STYLE),
  widget: CatchMomentsIconSX('menu-widget', ICON_STYLE),
  adCreative: CatchMomentsIconSX('photo-thumbnail', ICON_STYLE),
  /**
   * 🚨 **메뉴 하나에 그림 하나다** — 같은 그림을 두 메뉴가 나눠 쓰면 아이콘이 이름을 못 가리키고
   *   줄을 글자로만 읽게 된다. 메뉴를 더할 때는 이 표에서 **아직 안 쓰인 칸**을 고른다.
   * ⚠️ 그림은 **전부 선 아이콘**이다 — viewBox 24 · `fill="none"` · `stroke-width="2"` · 둥근 끝.
   *   `menu-*` 가 그 규격의 한 벌이다. 면으로 그린 것·굵기가 다른 것을 이 표에 섞지 마라.
   */
  home: CatchMomentsIconSX('menu-home', ICON_STYLE),
  contentHub: CatchMomentsIconSX('menu-contenthub', ICON_STYLE),
  creatorHub: CatchMomentsIconSX('menu-creatorhub', ICON_STYLE),
  brandHub: CatchMomentsIconSX('menu-brandhub', ICON_STYLE),
  deepSearch: CatchMomentsIconSX('menu-deepsearch', ICON_STYLE),
  idea: CatchMomentsIconSX('menu-idea', ICON_STYLE),
  seedingCheck: CatchMomentsIconSX('menu-seedingcheck', ICON_STYLE),
  postAnalysis: CatchMomentsIconSX('menu-postanalysis', ICON_STYLE),
  analytics: CatchMomentsIconSX('menu-analytics', ICON_STYLE),
  job: CatchMomentsIconSX('menu-job', ICON_STYLE),
  postBenchmark: CatchMomentsIconSX('menu-postbenchmark', ICON_STYLE),
  searchTrend: CatchMomentsIconSX('menu-searchtrend', ICON_STYLE),
  status: CatchMomentsIconSX('menu-status', ICON_STYLE),
  /**
   * 말풍선에 점 셋 — 메뉴 아이콘은 **전부 선 아이콘**이라 채워진 모양을 섞지 않는다
   * (viewBox 24 · `fill="none"` · `stroke-width="2"` · 둥근 끝).
   * ⚠️ 점 셋은 **길이 0 인 `<line>` + 둥근 끝**으로 그린다 — `stroke-linecap` 을 빼면 사라진다.
   * 브랜드 멘션(`menu-brandmention`)도 말풍선이라 그쪽은 모난 모양, 이쪽은 둥근 모양으로 가른다.
   */
  chat: CatchMomentsIconSX('menu-chat', ICON_STYLE),
  /** 이벤트 캘린더 갈래 — 같은 규격의 달력 그림. 위클리 하이라이트는 메뉴에 걸리지 않아 겹치지 않는다 */
  eventCalendar: CatchMomentsIconSX('menu-weekly', ICON_STYLE),
  /** 펼친 책 — 사용 가이드 */
  guide: CatchMomentsIconSX('menu-guide', ICON_STYLE),
};

/**
 * RESEARCH 줄의 그림 — Research 갈래 하나에 하나.
 * ⚠️ `ANALYSIS_TAB_ICONS`(Iconify solar)를 쓰지 않는다 — 굵기 1.5 의 다른 벌이라 위 표의 규격과 섞인다.
 * ⚠️ 판매몰 상품·Meta 광고는 `market`·`meta` 줄과 그림을 나눠 쓴다 — 그 둘은 메뉴에 걸리지 않는다.
 */
const RESEARCH_TAB_ICONS: Record<AnalysisTab, ReturnType<typeof CatchMomentsIconSX>> = {
  social: ICONS.deepSearch,
  ads: ICONS.adCreative,
  products: ICONS.mall,
  events: ICONS.eventCalendar,
};

// ----------------------------------------------------------------------

/** labs 이름 → 메뉴 줄 — 워크스페이스 `labs` 배열의 값이 열쇠다 */
const LABS_MENU_MAP = {
  'reddit-content': {
    title: '컨텐츠 플래너',
    path: paths.service.customService.redditContent,
    key: 'labs_reddit_content',
  },
  'brand-excel': {
    title: '멘션 통계',
    path: paths.service.customService.brandExcel,
    key: 'labs_brand_excel',
  },
  // 'content-ideation': {
  //   title: '컨텐츠 제안',
  //   path: paths.service.customService.contentIdeation,
  //   key: 'labs_content_ideation',
  // },
  'tracking-link': {
    title: '링크 추적',
    path: paths.service.customService.trackingLink,
    key: 'labs_tracking_link',
  },
};

/**
 * 워크스페이스의 labs 배열을 기반으로 Labs 메뉴 아이템을 생성합니다 — 배열에 적힌 줄만 그 차례로 선다(`'sns-performance'` 는 메뉴 줄이 아니라 심화 연동 스위치 — `workspace-labs`).
 * @param {Array} labsList - 워크스페이스의 labs 배열 (문자열 배열)
 * @returns {Array} Labs 메뉴 아이템 배열
 */
const getLabsMenuItems = (labsList: string[] = []) => {
  const names = (Array.isArray(labsList) ? labsList : [])
    .filter(labName => typeof labName === 'string' && labName.trim() !== '');

  return names
    .map(labName => LABS_MENU_MAP[labName as keyof typeof LABS_MENU_MAP])
    .filter(Boolean); // 매핑되지 않은 labs는 제외
};

// ----------------------------------------------------------------------

/**
 * Input nav data is an array of navigation section items used to define the structure and content of a navigation bar.
 * Each section contains a subheader and an array of items, which can include nested children items.
 *
 * Each item can have the following properties:
 * - `title`: The title of the navigation item.
 * - `path`: The URL path the item links to.
 * - `icon`: An optional icon component to display alongside the title.
 * - `info`: Optional additional information to display, such as a label.
 * - `allowedRoles`: An optional array of roles that are allowed to see the item.
 * - `caption`: An optional caption to display below the title.
 * - `children`: An optional array of nested navigation items.
 * - `disabled`: An optional boolean to disable the item.
 */

// export const icon = (name) => <SvgColor src={`${CONFIG.assetsDir}/assets/icons/navbar/${name}.svg`} />;



/**
 * 캠페인 리스트를 기반으로 키워드 모니터링 하위 메뉴 아이템을 생성합니다.
 * 🔑 세트가 없으면 「세트 만들기」 한 줄이 선다 — 추적 관리로 가서 **추적 등록 창**(키워드 탭)이 열린 채로 선다.
 * @returns 키워드 모니터링 하위 메뉴 아이템 배열
 */
const getKeywordMonitoringChildren = (campaignList: Record<string, any>[] = []) => {
  if (campaignList?.length > 0) {
    return sortKeywordCampaignsByCreatedAt(campaignList).map((campaign) => ({
      title: campaign.name,
      path: toKeywordMonitoringHref(String(campaign.id)),
      isCampaign: true,
    }));
  }

  return [
    {
      title: `${SET} 만들기`,
      path: toTrackIntentHref(paths.service.status, { kind: 'targetKeyword' }),
    },
  ];
};


// 네비게이션 메뉴 상수 정의
const SERVICE_MENU_ITEMS = [
  /**
   * 컨텐츠 허브 계열 셋 — 전부 검색과 같은 플랜 칸이다(`service-route-access` 의 규칙과 같아야 한다).
   * 컨텐츠 허브에서 Research·AI chat 으로 가는 길이 화면 안에도 있지만, 메뉴에서도 바로 열 수 있어야 한다.
   *
   * ⚠️ **분석(`/service/analytics`)은 메뉴에 걸지 않는다** — 화면과 라우트는 살아 있고 주소로
   *   열린다. 들어가는 길은 브랜드 허브·크리에이터 허브·AI 작업 안의 바로가기다.
   *   플랜 관문은 그대로 검색 칸이다(`service-route-access` 에 규칙이 남아 있다) —
   *   다시 걸 때 그 칸을 쓰고, 저장한 수집 세트를 아래 매다는 길도 함께 되살린다.
   * ⚠️ **컨텐츠 아이디어(`/service/idea`)도 메뉴에 걸지 않는다** — 같은 규약이다. 화면과
   *   라우트는 살아 있고 플랜 관문도 그대로다(`trending` 칸 · `service-route-access` 에 규칙이
   *   남아 있다). 다시 걸 때는 그 칸을 쓴다.
   *   🚨 **지금 그 화면은 주소로만 열린다** — 대시보드 바로가기에 줄이 남아 있으나 그 화면을
   *     어느 라우트도 그리지 않는다(A36). 「바로가기가 있으니 괜찮다」로 읽지 마라.
   */
  /**
   * 홈 — 서비스 첫 화면(서비스 뿌리 `/service` 가 곧 홈이다). 검색어 없이 열면 홈,
   *   말을 치면 「한 번에 찾기」 결과가 된다. 사이드바 **맨 위 묶음 없는 줄**로 선다(`getNavData`).
   */
  { title: '홈', path: paths.service.home, icon: ICONS.home, key: 'find', accessKey: IPlanService.key.search, accessField: IPlanService.search.searchYN },
  { title: '컨텐츠 허브', path: paths.service.searchAll, icon: ICONS.contentHub, key: 'searchAll', accessKey: IPlanService.key.search, accessField: IPlanService.search.searchYN },
  /** 주어별로 모아 보는 두 자리 — 허브 셋으로 「탐색」 맨 위에 함께 선다 */
  { title: '브랜드 허브', path: paths.service.brandHub, icon: ICONS.brandHub, key: 'brandHub', accessKey: IPlanService.key.search, accessField: IPlanService.search.searchYN },
  /**
   * 크리에이터 허브가 인플루언서 리스트를 **대체한다** — 그 화면의 조건 검색을 그대로 물었으므로
   * 플랜 칸도 그 화면의 것을 그대로 쓴다(검색 칸으로 열면 인플루언서 플랜 없이 발굴 목록이 열린다).
   */
  { title: '크리에이터 허브', path: paths.service.creators, icon: ICONS.creatorHub, key: 'creators', accessKey: IPlanService.key.influencer, accessField: IPlanService.influencer.influencerYN },
  /*
    🔑 **Research의 갈래 넷은 저마다 한 줄이다**(묶음 줄·하위 줄 없음) — 광고·상품 조사는 제 메뉴가
      없어졌고, 탭 안에만 있으면 「그런 기능이 있는지」를 모른다. 메뉴가 곧 목차다.
    ⚠️ 줄마다 **제 주소**다(`/service/research/<갈래>`) — 질의로 나누면 한 화면에서 네 줄이 같이 켜진다.
      켜지는 줄은 주소 하나가 정한다 — 갈래 없는 `/service/research` 는 소셜 갈래로 넘어간다.
    ⚠️ 플랜 칸은 넷 다 검색 칸이다 — `service-route-access` 의 `/service/research` 접두어 규칙과 같아야 한다.
  */
  ...ANALYSIS_TABS.map((tab) => ({
    title: ANALYSIS_TAB_LABELS[tab],
    path: paths.service.searchAllAnalysisTab(tab),
    icon: RESEARCH_TAB_ICONS[tab],
    key: `deepSearch:${tab}`,
    accessKey: IPlanService.key.search,
    accessField: IPlanService.search.searchYN,
  })),
  /**
   * 추적 관리는 **모든 플랜에 열린다** — 무엇을 추적할 수 있는지 보는 자리라 accessKey 를 비워 둔다.
   * 플랜 관문은 화면 안의 구역마다 선다(「… 플랜부터 제공됩니다」). 만료된 플랜은 TRACKING 의 다른 줄과 같이 잠긴다.
   */
  { title: '추적 관리', path: paths.service.status, icon: ICONS.status, key: 'status' },
  { title: '인플루언서 리스트', path: paths.service.influencer, icon: ICONS.influencer, key: 'influencer', accessKey: IPlanService.key.influencer, accessField: IPlanService.influencer.influencerYN },
  { title: '브랜드 멘션 추적', path: paths.service.brand, icon: ICONS.brand, key: 'brand', accessKey: IPlanService.key.brand, accessField: IPlanService.brand.brandYN },
  { title: '심화분석(베타)', path: paths.service.dataStats, icon: ICONS.labs, key: 'dataStats', accessKey: IPlanService.key.brand, accessField: IPlanService.brand.dataStatsYN },
  { title: '시딩 성과 모니터링', path: paths.service.campaign.root, icon: ICONS.campaign, key: 'campaign', accessKey: 'campaign', accessField: IPlanService.campaign.campaignYN },
  /** 컨텐츠 분석(Labs) — 글을 그룹으로 담아 AI 멀티모달 분석. 플랜 칸은 시딩과 같고, 워크스페이스 `labs` 에 `LAB_POST_ANALYSIS` 가 있어야 선다(평가 건수는 `task.postAnalysisMonthlyLimit`) */
  { title: '컨텐츠 분석', path: paths.service.labs.postAnalysis.root, icon: ICONS.postAnalysis, key: 'postAnalysis', accessKey: 'campaign', accessField: IPlanService.campaign.campaignYN },
  { title: '위젯 설정', path: paths.service.externalDashboard, icon: ICONS.widget, key: 'externalDashboard', accessKey: IPlanService.key.externalDashboard, accessField: IPlanService.externalDashboard.externalDashboardYN },
  // { title: 'Setting', path: paths.service.workspace, icon: ICONS.setting, key: 'workspace' },
  { title: '자사 계정 관리', path: paths.service.account, icon: ICONS.account, key: 'account', accessKey: IPlanService.key.brand, accessField: IPlanService.brand.brandYN },
  // { title: '포스팅 평가', path: paths.service.postBenchmark, icon: ICONS.postBenchmark, key: 'postBenchmark', accessKey: IPlanService.key.postBenchmark, accessField: IPlanService.postBenchmark.postBenchmarkYN },
  { title: '북마크', path: paths.service.bookmark.root, icon: ICONS.bookmark, key: 'bookmark', accessKey: IPlanService.key.bookmark, accessField: IPlanService.bookmark.bookmarkYN },
  { title: '시딩 전 체크', path: paths.service.influencerCheck, icon: ICONS.seedingCheck, key: 'influencerCheck', accessKey: IPlanService.key.influencerCheck, accessField: IPlanService.influencerCheck.influencerCheckYN },
];

/**
 * 플랜에 따라 메뉴의 비활성화 여부를 설정합니다.
 * @param {Object|null} planConfig - merge된 planService config
 * @param {boolean} isExpired - 만료 여부
 * @param {boolean} isDemo - 데모 모드 여부 (데모 시 서비스 메뉴는 활성화, 설정은 사이드바에서 wrap으로 제한)
 * @returns {Array} 메뉴 아이템 배열 (disabled 속성 포함)
 */
const getAccessibleMenuItems = (planConfig: any, isExpired: boolean, isDemo?: boolean) =>
  SERVICE_MENU_ITEMS.map(item => {
    // 접근 제한이 없는 메뉴는 항상 활성화
    if (!item.accessKey || !item.accessField) {
      return withPlanLock({ ...item, disabled: false });
    }

    // 만료되었으면 모든 항목 비활성화
    if (isExpired) {
      return withPlanLock({ ...item, disabled: true });
    }

    // 북마크는 플랜과 무관하게 항상 사용 가능
    if (item.key === 'bookmark') {
      return withPlanLock({ ...item, disabled: false });
    }

    if (isDemo && item.key === 'influencerCheck') {
      return withPlanLock({ ...item, disabled: true });
    }

    // 데모 모드에서는 플랜 config 무시하고 서비스 메뉴 활성화 (설정 탭은 NavUpgrade에서 wrap으로 제한)
    if (isDemo) {
      return withPlanLock({ ...item, disabled: false });
    }

    // merge된 planService config 사용 (route guard와 동일)
    if (!planConfig) {
      return withPlanLock({ ...item, disabled: true });
    }

    if (item.key === 'idea') {
      const keywordEnabled =
        planConfig[IPlanService.key.trending]?.[IPlanService.trending.trendingYN] === true;
      const thumbnailEnabled =
        planConfig[IPlanService.key.influencer]?.[IPlanService.influencer.influencerYN] === true;

      return withPlanLock({
        ...item,
        disabled: !(keywordEnabled || thumbnailEnabled),
      });
    }

    // 플랜 설정에서 해당 기능의 활성화 여부 확인
    const serviceConfig = planConfig[item.accessKey];
    if (!serviceConfig) {
      return withPlanLock({ ...item, disabled: true });
    }

    const isEnabled = serviceConfig[item.accessField] === true;
    return withPlanLock({ ...item, disabled: !isEnabled });
  })
  ;

/**
 * 키 순서대로 접근 가능한 메뉴 아이템을 반환합니다.
 * @param {Array} menuItems - 접근 가능한 메뉴 아이템 배열
 * @param {Array<string>} keys - 메뉴 키 순서
 * @returns {Array} 정렬된 메뉴 아이템 배열
 */
const pickMenuItems = (menuItems: Record<string, any>[], keys: string[]) =>
  keys
    .map((key) => menuItems.find((item) => item.key === key))
    .filter(Boolean);

/**
 * 네비게이션 데이터를 생성하는 함수
 * @returns {Array} 네비게이션 데이터 배열
 */
/**
 * @param liveMenuSets 「내 화면」 줄의 **살아 있는 목록** — 주지 않으면 `user` 가 실어 온 것을 쓴다.
 * @param liveMenuJobs 「내 작업」의 **살아 있는 목록** — 같은 규약이다.
 * @param liveHiddenTasks TASK SUPPORT 에서 **감춘 붙박이** — 같은 규약이다.
 *   🚨 저장 직후 사이드바가 안 바뀌던 자리다 — `user` 는 로그인 때 한 번 실려 오는 값이라
 *   줄을 저장해도 그 안의 `menuSets` 는 옛것이다. 부르는 쪽이 구독한 목록을 넘긴다.
 */
export const getNavData = (
  user: AuthUser | null | undefined,
  isDemo?: boolean,
  liveMenuSets?: MenuSet[] | null,
  liveMenuJobs?: MenuJob[] | null,
  liveHiddenTasks?: string[] | null,
  /**
   * 살아 있는 수집 세트 문서 — **`accounts` 까지** 든 것.
   * 🚨 워크스페이스의 `campaignList` 는 세션이 실어 온 파생 스냅숏이라 계정이 빠져 있다.
   *   그것으로 갈래를 재면 계정만 건 세트가 **멘션 추적으로 떨어진다**(실측). 추적 관리의
   *   「내 화면」은 구독본을 봐서 계정 추적으로 옳게 갈랐고, 둘이 어긋났다.
   */
  liveCampaigns?: Record<string, any>[] | null,
  /** 초기 설정 진행 「3/4」 — 「추적 관리」 줄 옆에 단다(`useOnboardingBadge`) · 없으면 안 단다 */
  trackingBadge?: string | null,
  /** 이번 달 Research 「쓴 수/한도」 — RESEARCH 표제 줄 오른쪽 끝에 단다(`useResearchUsage` · 한도가 있는 플랜만) · 없으면 안 단다 */
  researchBadge?: string | null,
) => {



  // campaignList·labs 는 워크스페이스 문서에 실재하나 UserWorkspace 선언에 빠져 있다(공용부 타입 — A번호 후보). 국소 좁힘 (로직 무변)
  const currentWorkspace = findCurrentWorkspace(user) as
    | (Record<string, any> & { campaignList?: Record<string, any>[]; labs?: string[] })
    | null
    | undefined;
  const campaignList = liveCampaigns ?? currentWorkspace?.campaignList ?? [];
  const currentSubscriptionPlan = currentWorkspace?.subscriptionPlan || null;
  const labsList = currentWorkspace?.labs || []; // labs 배열 추가

  const isExpired = currentSubscriptionPlan?.planStatus === 'expired' || false;
  const planId = resolveSubscriptionPlanId(currentSubscriptionPlan);

  const planConfig: any = planId && currentSubscriptionPlan && !isExpired
    ? buildEffectivePlanConfig(currentSubscriptionPlan, planId)
    : null;

  // 플랜에 따라 접근 가능한 메뉴 필터링 (isDemo 시 서비스 메뉴 활성화, 설정은 NavUpgrade에서 wrap 제한)
  const accessibleMenuItems = getAccessibleMenuItems(planConfig, isExpired, isDemo);

  const isKeywordAccessible = planConfig?.[IPlanService.key.keyword]?.[IPlanService.keyword.keywordYN] === true && !isExpired;

  const keywordMonitoringChildren = getKeywordMonitoringChildren(campaignList);
  /*
    🔑 **캠페인은 이 줄의 하위로 선다** — 줄을 펴면 걸어 둔 캠페인들이 나온다.
    ⚠️ 세트가 하나도 없으면 「세트 만들기」 한 줄이 그 자리에 선다(`getKeywordMonitoringChildren`).
  */
  const keywordMonitoringItem = withPlanLock({
    title: '타겟 멘션 추적',
    key: 'keywordMonitoring',
    path: paths.service.keywordMonitoring,
    icon: ICONS.keywordMonitoring,
    /* 🚨 캠페인들이 이 줄의 «갈래»다 — 접어 두면 걸어 둔 캠페인이 있다는 것을 못 본다 */
    alwaysOpen: true,
    // ⚠️ 부모가 잠기면 세트 줄도 흐리게 + 자물쇠 — 잠긴 부모 밑에 열린 것처럼 보이면 눌러 보고서야 막힌 줄 안다
    children: keywordMonitoringChildren.map(item => withPlanLock({
      ...item,
      disabled: !isKeywordAccessible,
    })),
    defaultOpen: !isExpired,
    disabled: !isKeywordAccessible,
  });

  // Labs 메뉴 아이템 생성
  const labsMenuItems = getLabsMenuItems(labsList);
  const isLabsAccessible = planConfig?.[IPlanService.key.customService]?.[IPlanService.customService.customServiceYN] === true && !isExpired;

  /**
   * Labs — 커스텀 고객용 줄에 **아직 다듬는 것**(심화분석·위젯 설정)을 함께 세운다.
   *
   * 🔑 **Labs 는 켜진 줄만 선다 — 자물쇠를 달지 않는다.** 커스텀 줄은 `labs` 배열 + `customServiceYN`,
   *   심화분석·위젯 설정은 제 플랜 칸이 열려 있을 때만 선다. 다른 구역은 잠긴 줄을 자물쇠로 보인다.
   * ⚠️ **관문을 덮어쓰지 않는다** — 뒤 둘은 `getAccessibleMenuItems` 가 제 플랜 칸으로 판정한 것이라
   *   `isLabsAccessible` 로 거르면 **남의 관문(커스텀)** 으로 바뀐다.
   */
  const labsSectionItems: Record<string, any>[] = isLabsAccessible
    ? labsMenuItems.map((item) => ({ ...item, icon: ICONS.labs, disabled: false }))
    : [];


  const workspaceId = toCurrentWorkspaceId(currentWorkspace, user?.lastWorkspace);
  const searchTrendItem = isSearchTrendFeatureWorkspace(workspaceId)
    ? {
        title: '검색량 트렌드',
        key: 'searchTrend',
        path: paths.service.searchTrend,
        icon: ICONS.searchTrend,
        disabled: false,
      }
    : null;

  /**
   * DISCOVER — 무엇이 돌고 있나 훑는 연장들.
   *
   * 🚨 **차례를 정하는 곳은 여기다** — `SERVICE_MENU_ITEMS` 의 줄 차례가 아니라 **이 열쇠 목록**이
   *   화면에 서는 차례다. 위에서 줄을 옮겨도 여기를 안 고치면 화면은 그대로다.
   * 🔑 **허브 셋이 맨 위에 이 차례로 선다** — 컨텐츠(무엇이 도나) → 브랜드(누가 파나) → 크리에이터(누가 만드나).
   *   셋이 한 벌이므로 **다른 묶음으로 흩지 마라** — 흩어 놓으면 셋이 한 벌인 것이 화면에서 안 보인다.
   * ⚠️ 플랜 칸은 셋이 서로 다르다 — 크리에이터 허브만 `influencer` 다. 같은 묶음에 둔다고 같은 칸이 되지 않는다.
   * ⚠️ 소셜 통합 검색·상품 정보 조사·광고 소재 조사·이벤트 캘린더는 **제 줄이 없다** — Research 의 갈래다.
   *   그 옛 주소(`/service/search` · `/service/market` · `/service/meta` · `/service/event`)는 `legacy-redirects` 가 Research 갈래로 보낸다.
   * ⚠️ 인플루언서 리스트도 **메뉴에 없다** — 크리에이터 허브의 「크리에이터 찾기」가 그 화면을 통째로 물고 있다.
   *   그 라우트(`/service/influencer`)는 그대로 둔다 — 기존 링크가 살아 있어야 한다.
   */
  const discoverItems = [...pickMenuItems(accessibleMenuItems, [
    'searchAll',
    'brandHub',
    'creators',
    // ⚠️ 북마크는 **훑다가 담아 둔 것을 되찾는 자리**라 훑는 연장들 끝에 붙는다
    'bookmark',
  ])];

  /**
   * RESEARCH — 조건을 걸고 파고드는 조사 화면. 갈래 넷이 저마다 한 줄로 **DISCOVER 바로 아래** 선다.
   * ⚠️ 차례는 `ANALYSIS_TABS` 의 차례다(소셜 게시물 → Meta 광고 → 판매몰 상품 → 이벤트 캘린더).
   */
  const researchItems = pickMenuItems(
    accessibleMenuItems,
    ANALYSIS_TABS.map((tab) => `deepSearch:${tab}`),
  );

  /**
   * 기본 셋 — 붙박이 줄 셋. 브랜드 멘션·타겟 키워드는 TRACKING, 자사 계정은 OPERATIONS 에 선다.
   *
   * 🚨 **플랜 관문은 줄이 아니라 «여는 화면»의 것이다** — 항목을 통째로 가져다 쓰므로
   *   아이콘·관문·하위 목록이 함께 따라온다.
   * ⚠️ 워크스페이스가 제 줄을 정하는 길(`menuSets`)은 **메뉴에 걸려 있지 않다** — 저장과
   *   화면은 살아 있고, 다시 걸 때 이 표를 그대로 쓴다.
   */
  const builtinMenuItems: Record<string, Record<string, any> | null | undefined> = {
    brandMention: accessibleMenuItems.find((item) => item.key === 'brand'),
    keywordMonitoring: keywordMonitoringItem,
    brandAccount: accessibleMenuItems.find((item) => item.key === 'account'),
  };

  /*
    🔑 **한 층으로 편다** — 추적 관리 하위에 줄들이 나란히 선다. 타겟 키워드 줄이 캠페인을
      제 하위로 또 물고 있으면 세 층이 되고, 캠페인 하나 열려고 두 번 펴야 한다.
    🚨 **타겟 키워드 줄은 제 줄을 안 세우고 «캠페인들이 그 자리에 선다».** 그 줄이 여는 화면은
      «캠페인을 안 고른» 같은 화면이고, 거기서 할 수 있는 일 둘이 다른 데서 이미 된다 —
      고르기는 이 목록이, 새로 만들기는 추적 관리의 타겟 키워드 구역이 한다.
      ⚠️ 줄이 사라져도 **「내 화면」의 그 줄은 남는다** — 이름·켜고 끄기·차례가 캠페인 묶음을
      통째로 움직인다(끄면 캠페인이 다 사라지고, 올리면 다 같이 올라간다).
      ⚠️ 세트가 하나도 없으면 「세트 만들기」 한 줄이 그 자리에 선다(`getKeywordMonitoringChildren`).
    🔑 **하위에는 아이콘을 안 붙인다** — 층이 얕고 이름이 곧 구분이라, 아이콘이 붙으면 줄마다
      다른 그림이 서서 목록이 시끄러워진다(플랜 배지는 남는다 — 그것은 «못 연다»를 말한다).
    ⚠️ **차례는 「내 화면」 줄의 차례다**(추적 관리 화면의 ▲▼) — 캠페인은 제 줄을 따라 움직인다.
  */



  /**
   * TRACKING — **걸어 두면 계속 쌓이는 것들**. 한 번 조건을 박아 두고 뒤에서 도는 자리다.
   *
   * ```
   *   추적 관리                무엇을 걸어 두었나 — 모든 플랜에 열린다
   *   브랜드 멘션 추적          남들이 우리를 말한 글
   *   타겟 멘션 추적           캠페인마다 건 조회어 — 캠페인들이 하위로 선다
   * ```
   *
   * ⚠️ 자사 계정 관리(«우리가 올린 글»)는 여기 없다 — 우리 것을 돌보는 자리라 OPERATIONS 맨 위에 선다.
   *   모수도 다르다(브랜드 멘션은 자사 계정 글을 **뺀다**).
   */

  /**
   * 갈래 하나를 줄로 세운다.
   *
   * 🚨 **하위가 하나여도 묶음을 세운다** — 갈래는 «무엇을 보는가»의 이름이라, 하나일 때만
   *   층이 사라지면 화면 하나를 더 걸었을 때 메뉴 모양이 통째로 바뀐다. 늘 같은 자리에
   *   같은 모양으로 있어야 「내가 건 것이 어디 서는지」를 안다.
   * ⚠️ 하위가 **하나도 없으면** 묶음 자체를 안 세운다 — 빈 묶음은 「고장 난 메뉴」로 읽힌다.
   */

  /* 🔑 추적 관리가 TRACKING 맨 위다 — 거는 자리가 걸어 둔 것들 앞에 선다 */
  const statusItem = accessibleMenuItems.find((item) => item.key === 'status');
  const trackingItems = [
    statusItem
      ? withPlanLock({ ...statusItem, disabled: isExpired, ...(trackingBadge && !isExpired ? { info: progressMark(trackingBadge) } : {}) })
      : null,
    builtinMenuItems.brandMention,
    keywordMonitoringItem,
    searchTrendItem,
  ].filter(Boolean);

  /**
   * OPERATIONS — **우리 것을 돌보는 자리**. 자사 계정 관리가 맨 위에 서고, 그 아래 시딩 두 줄이 선다(컨텐츠 분석은 Labs).
   * 🔑 시딩은 **한 건을 처음부터 끝까지** 따라가는 차례다: 고를 사람 검수 → 집행 중 성과(AI 영상 평가 포함).
   * 🔑 시딩은 **브랜드부터**다 — 검수(`influencerCheck`)와 성과(`campaign`)가 같은 계단에 선다.
   *   아래 플랜은 두 줄 다 잠금 표시로 서고, 누르면 업그레이드 안내가 뜬다.
   * ⚠️ 데모에서는 시딩 전 체크를 아예 뺀다 — 비활성으로 두지 않고 안 보인다.
   */
  const operationsItems = [
    builtinMenuItems.brandAccount,
    ...pickMenuItems(
      accessibleMenuItems,
      isDemo ? ['campaign'] : ['influencerCheck', 'campaign'],
    ),
  ].filter(Boolean);

  /*
    🔑 **아직 다듬는 것은 Labs 에 모은다** — 심화분석(베타)·위젯 설정. 제 구역을 세울 만큼
      쓰이지 않아서다. **그 워크스페이스에서 켜져 있을 때만** 선다(맞춤 플랜이면 저장된 planService 기준) —
      못 여는 줄은 자물쇠 없이 빠진다. 주소로 들어오면 `service-route-access` 가 막는다.
  */
  /*
    🔑 **컨텐츠 분석은 실험 기능이다** — 워크스페이스 `labs` 에 `LAB_POST_ANALYSIS` 가 있고 플랜 칸(시딩과 같은 `campaignYN`)이
      열렸을 때만 선다. 커스텀 칸(`customServiceYN`)은 보지 않는다. 주소로 들어오면 화면이 `labs` 를 다시 본다.
  */
  if (hasWorkspaceLab(labsList, LAB_POST_ANALYSIS)) {
    labsSectionItems.push(
      ...pickMenuItems(accessibleMenuItems, ['postAnalysis'])
        .filter((item): item is Record<string, any> => Boolean(item) && !item?.disabled),
    );
  }
  labsSectionItems.push(
    ...pickMenuItems(accessibleMenuItems, ['dataStats', 'externalDashboard'])
      .filter((item): item is Record<string, any> => Boolean(item) && !item?.disabled),
  );


  /*
    ⚠️ **정기물 묶음(「인사이트」)은 메뉴에 없다** — 위클리 하이라이트(`/service/weekly`)·
      TOP 브랜드 벤치마킹(`/service/trend`) 둘의 화면과 라우트는 살아 있고 주소로 열린다.
      다시 걸 때 둘 다 `report` 플랜 칸이다.
  */

  /*
    🚨 **대시보드는 «안 그려진다».** 서비스 뿌리(`/service`)가 홈을 그리고 `/service/dashboard` 는 홈으로
      보낸다 — `ServiceRoot` 를 부르는 곳이 없다(A36).
    ⛔ 그래서 **「대시보드 바로가기로 갈 수 있다」를 근거로 삼지 마라** — 위클리 하이라이트·
      TOP 브랜드 벤치마킹·컨텐츠 아이디어는 지금 **주소로만** 열린다.
  */
  /** 홈 — 묶음 이름 없이 맨 위 한 줄 */
  const homeItems = pickMenuItems(accessibleMenuItems, ['find']);

  /**
   * GUIDE — 화면마다의 사용법(`/guide` — 로그인 없이도 열리는 공개 화면). 관문은 가이드 화면·API 와 같은 `canReadManual` 이다 —
   *   문턱(`GUIDE_MIN_PLAN`)이 없으면 늘 열리고, 생기면 못 넘는 사람에게 자물쇠가 붙는다.
   */
  const guideItem = withPlanLock({
    title: '사용 가이드',
    key: 'guide',
    path: paths.guide.root,
    icon: ICONS.guide,
    disabled: !canReadManual(planViewerOfUser(user)),
  });

  return [
    ...(homeItems.length ? [{ items: homeItems }] : []),
    ...(discoverItems.length ? [{ subheader: 'Discover', items: discoverItems }] : []),
    ...(researchItems.length ? [{ subheader: 'Research', items: researchItems, ...(researchBadge ? { info: usageMark(researchBadge) } : {}) }] : []),
    ...(trackingItems.length ? [{ subheader: 'Tracking', items: trackingItems }] : []),
    ...(operationsItems.length ? [{ subheader: 'Operations', items: operationsItems }] : []),
    /* ⚠️ 켜진 줄이 하나도 없으면 Labs 제목도 안 선다 — 빈 구역·자물쇠 구역을 세우지 않는다 */
    ...(labsSectionItems.length
      ? [{ subheader: 'Labs', items: labsSectionItems }]
      : []),
    { subheader: 'Guide', items: [guideItem] },
  ];
};

/** 비회원 껍데기에서 잠긴 줄 하나 — 누르면 로그인 안내가 이 값을 받는다 */
export type GuestMenu = { key: string; title: string; path: string };

/**
 * **비회원 껍데기(질문 대화 `/ask`)의 사이드바** — 로그인한 새 사람이 보는 목록(`getNavData(null)`)과 같은 구역·이름·그림·차례.
 * 🔑 줄은 전부 잠긴다(흐림 + 자물쇠 · 눌린다) — 누르면 `onLocked` 가 로그인 안내를 연다.
 *   줄의 `path`(서비스 주소)가 로그인 뒤 돌아갈 곳이다. 하위 줄도 같은 규칙이다.
 * ⚠️ 워크스페이스를 읽지 않는다 — 사용자 없이 짓는다.
 * @param onLocked 잠긴 줄을 눌렀을 때
 */
export const getGuestNavData = (onLocked: (menu: GuestMenu) => void) =>
  getNavData(null).map((section) => ({
    ...section,
    items: (section.items as Record<string, any>[]).map((item) => toGuestNavItem(item, onLocked)),
  }));

/** 줄 하나를 잠근다 — 하위 줄까지 */
const toGuestNavItem = (item: Record<string, any>, onLocked: (menu: GuestMenu) => void): Record<string, any> => {
  const path = String(item.path ?? '');
  return {
    ...item,
    disabled: false,
    locked: true,
    info: PLAN_LOCK_MARK,
    onClick: () => onLocked({ key: String(item.key ?? item.title ?? ''), title: String(item.title ?? ''), path }),
    ...(Array.isArray(item.children) ? { children: item.children.map((child: Record<string, any>) => toGuestNavItem(child, onLocked)) } : {}),
  };
};

/**
 * **숨김 전 전체 메뉴** — 모든 플랜 칸 · 모든 labs 가 켜진 워크스페이스로 `getNavData` 를 돌린 목록.
 * 🔑 화면 머리의 폴백이다(`resolveNavHeading`) — 플랜·labs 로 사이드바에서 빠진 화면을 주소로 열어도
 *   구역·메뉴 이름이 선다. 사이드바와 같은 함수를 돌리므로 이름이 따로 놀지 않는다.
 * ⚠️ 워크스페이스 한정 줄(검색량 트렌드)과 세트 줄은 여기 없다 — 그 둘은 살아 있는 목록에서만 고른다.
 */
export const getFullNavData = () => {
  const planService: Record<string, Record<string, boolean>> = {
    [IPlanService.key.keyword]: { [IPlanService.keyword.keywordYN]: true },
    [IPlanService.key.customService]: { [IPlanService.customService.customServiceYN]: true },
  };
  SERVICE_MENU_ITEMS.forEach((item) => {
    if (!item.accessKey || !item.accessField) return;
    planService[item.accessKey] = { ...planService[item.accessKey], [item.accessField]: true };
  });
  const user = {
    lastWorkspace: 'nav-full',
    workspaces: [{
      id: 'nav-full',
      labs: [...Object.keys(LABS_MENU_MAP), LAB_POST_ANALYSIS],
      campaignList: [],
      subscriptionPlan: { planId: 'plan_enterprise', planStatus: 'active', planService },
    }],
  };
  return getNavData(user as unknown as AuthUser);
};
